import React, { useEffect, useRef, useState } from 'react';
import {
    MapPin, AlertTriangle, ExternalLink, LogIn, LogOut, CheckCircle2, Loader2,
} from 'lucide-react';
import { loadGoogleMaps, isMapsConfigured, formatDistance } from '../utils/googleMaps';

/**
 * Where the carer was at each end of a visit, relative to the client's home.
 *
 * Map on the left, one card per phase on the right, and — when either end fell
 * outside the expected radius — a review banner with a manager sign-off action.
 *
 * Degrades in layers, because none of this data is guaranteed:
 *   - no API key / SDK fails  → cards + "open in Maps" link, no map
 *   - carer has no fix        → that card reads "not captured"
 *   - client has no pin       → distances unknown, nothing is alleged
 *   - visit still running     → no clock-out card
 *
 * Also used for a care-home SHIFT (`subject="shift"`, from the rota's Shift
 * Attendance Details): just the carer's clock-in and clock-out pins — there is
 * no client home, no distance and nothing to approve — and the clock-out card
 * appears once `location.ended` says the shift has finished.
 */

// Inline SVG pin — no external image requests, and it reads correctly on both
// light and dark map tiles.
const pinIcon = (maps, fill) => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z',
    fillColor: fill,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 1.5,
    scale: 1.5,
    anchor: new maps.Point(12, 22),
});

const CLIENT_COLOUR = '#7C3AED'; // violet — the client's home
const START_COLOUR = '#10B981';  // green  — clock-in
const END_COLOUR = '#F97316';    // orange — clock-out

/**
 * One end of the visit: coloured icon, distance headline, accuracy beneath —
 * plus its own review banner and sign-off, since clock-in and clock-out are
 * approved independently.
 */
/**
 * Why the app had no location, when it said (api shift.controller sessionFix
 * <prefix>_location_failure). Nothing said = an app from before this, or a
 * manager-entered shift: "No location recorded".
 */
const MISSING_REASON = {
    serviceDisabled: 'Location was turned off on the phone',
    permissionDenied: 'The app was not allowed to use location',
    permissionDeniedForever: 'Location is blocked for the app in phone settings',
    timeout: 'No location signal in time (often indoors)',
    error: 'The phone could not read its location',
};

const PhaseCard = ({
    icon: Icon, colour, label, point, distance, missingReason,
    needsReview, approved, radius, onApprove, approving, error,
}) => {
    const distanceLabel = formatDistance(distance);

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#262b33] px-3.5 py-3">
            <div className="flex items-center gap-3">
                <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                    style={{ backgroundColor: `${colour}1A`, color: colour }}
                >
                    <Icon size={17} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-bold text-gray-800 dark:text-gray-100">
                        {point
                            ? `${label} ${distanceLabel ?? ''}${distanceLabel ? ' away' : ''}`.trim()
                            : `${label} — not captured`}
                    </div>
                    <div className="text-[11.5px] text-gray-400 dark:text-gray-500">
                        {point
                            ? (point.accuracy_m
                                ? `Accuracy ±${Math.round(point.accuracy_m)} m`
                                : 'Accuracy not reported')
                            : (MISSING_REASON[missingReason] || 'No location recorded')}
                    </div>
                </div>
                {approved && (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                        <CheckCircle2 size={11} /> Approved
                    </span>
                )}
            </div>

            {/* Review + sign-off for THIS end only. */}
            {needsReview && !approved && (
                <div className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 dark:border-amber-900 dark:bg-amber-900/20">
                    <div className="flex items-start gap-1.5 text-[11.5px] font-medium text-amber-800 dark:text-amber-300">
                        <AlertTriangle size={13} className="mt-px shrink-0" />
                        <span>Outside the expected {radius} m area. Review before approval.</span>
                    </div>
                    {onApprove && (
                        <button
                            onClick={onApprove}
                            disabled={approving}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-amber-700 disabled:opacity-60"
                        >
                            {approving
                                ? <Loader2 size={12} className="animate-spin" />
                                : <CheckCircle2 size={12} />}
                            {approving ? 'Approving…' : `Approve ${label.toLowerCase()}`}
                        </button>
                    )}
                    {error && (
                        <p className="mt-1.5 text-[11px] text-rose-600 dark:text-rose-400">{error}</p>
                    )}
                </div>
            )}
        </div>
    );
};

const VisitLocationMap = ({ location, onApproved, subject = 'visit' }) => {
    const isShift = subject === 'shift';
    const mapRef = useRef(null);
    const [mapFailed, setMapFailed] = useState(false);
    // Which phase is mid-request ('start' | 'end' | null), plus a per-phase
    // error, so approving the clock-in can't spin or fail the clock-out card.
    const [approvingPhase, setApprovingPhase] = useState(null);
    const [errors, setErrors] = useState({});

    const client = location?.client || null;
    const start = location?.carer || null;
    const end = location?.carer_end || null;
    const radius = location?.on_site_radius_m ?? 100;
    const visitLogId = location?.visit_log_id || null;

    const canMap = isMapsConfigured() && (client || start || end) && !mapFailed;

    useEffect(() => {
        if (!canMap || !mapRef.current) return undefined;

        let cancelled = false;
        let overlays = [];

        loadGoogleMaps()
            .then((maps) => {
                if (cancelled || !mapRef.current) return;

                const map = new maps.Map(mapRef.current, {
                    zoom: 16,
                    center: client || start || end,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    // Keep the surface calm — this is a verification aid, not an
                    // exploration map.
                    clickableIcons: false,
                    gestureHandling: 'cooperative',
                });

                const bounds = new maps.LatLngBounds();
                let plotted = 0;

                const plot = (point, colour, title, zIndex) => {
                    if (!point) return;
                    overlays.push(new maps.Marker({
                        map,
                        position: { lat: point.latitude, lng: point.longitude },
                        icon: pinIcon(maps, colour),
                        title,
                        zIndex,
                    }));
                    bounds.extend({ lat: point.latitude, lng: point.longitude });
                    plotted += 1;

                    // Shade the reported GPS accuracy so a wide circle explains
                    // an apparently large distance.
                    if (point.accuracy_m > 0) {
                        overlays.push(new maps.Circle({
                            map,
                            center: { lat: point.latitude, lng: point.longitude },
                            radius: point.accuracy_m,
                            strokeColor: colour,
                            strokeOpacity: 0.35,
                            strokeWeight: 1,
                            fillColor: colour,
                            fillOpacity: 0.1,
                        }));
                    }
                };

                // The expected area around the client's home, so "inside" vs
                // "outside" is something the manager can see rather than infer.
                if (client) {
                    overlays.push(new maps.Circle({
                        map,
                        center: { lat: client.latitude, lng: client.longitude },
                        radius,
                        strokeColor: CLIENT_COLOUR,
                        strokeOpacity: 0.4,
                        strokeWeight: 1,
                        fillColor: CLIENT_COLOUR,
                        fillOpacity: 0.07,
                    }));
                }

                plot(client, CLIENT_COLOUR, `Client home${client?.address ? ` — ${client.address}` : ''}`, 1);
                plot(start, START_COLOUR, 'Carer at clock-in', 2);
                plot(end, END_COLOUR, 'Carer at clock-out', 3);

                if (plotted > 1) {
                    map.fitBounds(bounds, 40);
                    // fitBounds on very close points zooms in absurdly far.
                    maps.event.addListenerOnce(map, 'idle', () => {
                        if (map.getZoom() > 18) map.setZoom(18);
                    });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('[VisitLocationMap] map unavailable:', err.message);
                setMapFailed(true);
            });

        return () => {
            cancelled = true;
            overlays.forEach((o) => o.setMap(null));
            overlays = [];
        };
    }, [canMap, client, start, end, radius]);

    /** Sign off one end of the visit. `phase` is 'start' or 'end'. */
    const approve = async (phase) => {
        if (!visitLogId || approvingPhase) return;
        setApprovingPhase(phase);
        setErrors((e) => ({ ...e, [phase]: null }));
        try {
            // Imported lazily so this component stays usable anywhere the
            // schedule service isn't already loaded.
            const { domiciliaryScheduleService } = await import('../services');
            const res = await domiciliaryScheduleService.approveVisitLocation(visitLogId, phase);
            if (res?.success) {
                // Refetch rather than flipping local state — the server is the
                // authority on what's approved.
                onApproved?.();
            } else {
                setErrors((e) => ({ ...e, [phase]: res?.message || 'Could not approve' }));
            }
        } catch (err) {
            setErrors((e) => ({
                ...e,
                [phase]: err?.response?.data?.message || 'Could not approve',
            }));
        } finally {
            setApprovingPhase(null);
        }
    };

    // Link out so a manager can inspect the spot properly even without an
    // embedded map. Prefer the clock-in point.
    const linkTarget = start || end || client;
    const externalUrl = linkTarget
        ? `https://www.google.com/maps/search/?api=1&query=${linkTarget.latitude},${linkTarget.longitude}`
        : null;

    return (
        <div>
            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Location
                </h3>
                {externalUrl && (
                    <a
                        href={externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        Open in Maps <ExternalLink size={10} />
                    </a>
                )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
                {/* Map */}
                <div className="sm:w-1/2">
                    {canMap ? (
                        <div
                            ref={mapRef}
                            className="h-[172px] w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#31373f]"
                        />
                    ) : (
                        <div className="grid h-[172px] w-full place-items-center rounded-xl border border-dashed border-gray-200 dark:border-gray-800 px-3 text-center text-[11px] text-gray-500 dark:text-gray-400">
                            {!isMapsConfigured() || mapFailed
                                ? 'Map preview unavailable — use “Open in Maps”.'
                                : `No coordinates recorded for this ${subject}.`}
                        </div>
                    )}
                </div>

                {/* Phase cards + review banner */}
                <div className="flex flex-1 flex-col gap-2">
                    <PhaseCard
                        icon={LogIn}
                        colour={START_COLOUR}
                        label="Clock-in"
                        point={start}
                        missingReason={location?.start_failure}
                        distance={location?.distance_m}
                        needsReview={!!location?.start_needs_review}
                        approved={!!location?.start_approved}
                        radius={radius}
                        onApprove={visitLogId ? () => approve('start') : null}
                        approving={approvingPhase === 'start'}
                        error={errors.start}
                    />
                    {/* Only claim something about the end once the visit finished. */}
                    {(isShift ? !!location?.ended : (end || location?.end_verdict !== 'no_location')) && (
                        <PhaseCard
                            icon={LogOut}
                            colour={END_COLOUR}
                            label="Clock-out"
                            point={end}
                            missingReason={location?.end_failure}
                            distance={location?.end_distance_m}
                            needsReview={!!location?.end_needs_review}
                            approved={!!location?.end_approved}
                            radius={radius}
                            onApprove={visitLogId ? () => approve('end') : null}
                            approving={approvingPhase === 'end'}
                            error={errors.end}
                        />
                    )}
                </div>
            </div>

            {/* Legend — keeps the three pins unambiguous. */}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px] text-gray-500 dark:text-gray-400">
                {!isShift && (
                    <span className="flex items-center gap-1.5">
                        <MapPin size={11} style={{ color: CLIENT_COLOUR }} />
                        {client ? (client.address || 'Client home') : 'Client address not pinned'}
                    </span>
                )}
                <span className="flex items-center gap-1.5">
                    <MapPin size={11} style={{ color: START_COLOUR }} /> Clock-in
                </span>
                <span className="flex items-center gap-1.5">
                    <MapPin size={11} style={{ color: END_COLOUR }} /> Clock-out
                </span>
            </div>
        </div>
    );
};

export default VisitLocationMap;
